import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';
import { browserTracingIntegration } from '@sentry/react';
import { Config } from 'config';
import getEnvironmentName from '../utils/getEnvironmentName';
import { authReducerSelector } from '../../../store/auth/auth.selectors';
import { accountIdSelector } from '../../../store/account/account.selectors';
import sentryContexts from '../constants/sentryContexts.constant';
import shouldFilterEvent from '../utils/shouldFilterEvent';

const useSentry = () => {
  const authState = useSelector(authReducerSelector);
  const accountId = useSelector(accountIdSelector);

  useEffect(() => {
    const environmentName = getEnvironmentName();
    const sentryClientHub = Sentry.getClient();

    if (environmentName && !sentryClientHub && Config.SENTRY_DSN) {
      const release = process.env.VITE_APP_RELEASE_NUMBER || '1.0.0.1';
      Sentry.init({
        ignoreErrors: ['Non-Error promise rejection captured', 'Non-Error exception captured'],
        dsn: Config.SENTRY_DSN,
        tracesSampleRate: Config.IS_PRODUCTION ? 0.1 : 0,
        sampleRate: 1.0,
        environment: environmentName,
        integrations: [
          Sentry.breadcrumbsIntegration({ console: false }),
          browserTracingIntegration({})
        ],
        normalizeDepth: 15,
        release,
        beforeSend: (event) => {
          if (shouldFilterEvent(event)) {
            return null;
          }

          return event;
        }
      });
    }
  }, []);

  useEffect(() => {
    if (authState.id) {
      Sentry.setUser({
        id: authState.id,
        role: authState.role,
        permissions: Object.values(authState.permissions)
      });
    }
  }, [authState]);

  useEffect(() => {
    if (accountId) {
      Sentry.setContext(sentryContexts.account, {
        accountId
      });
      Sentry.setTags({ accountId });
    }
  }, [accountId]);
};

export default useSentry;
